import PropTypes from 'prop-types';
import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';

import { LandlordScoringShape } from '@spotahome/ui-library/src/tenant/constants/shapes';
import { DSIcon } from '@spotahome/ui-library/src/base';
import { yearsSinceDate } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';
import PrivateLandlord from '@spotahome/ui-library/src/tenant/components/icons/PrivateLandlord';
import ProfessionalLandlord from '@spotahome/ui-library/src/tenant/components/icons/ProfessionalLandlord';
import Scoring from '@spotahome/ui-library/src/tenant/components/Scoring';
import Tag from '@spotahome/ui-library/src/tenant/components/Tag';
import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';
import {
  PRIVATE_LANDLORD,
  PROFESSIONAL_LANDLORD
} from '@spotahome/ui-library/src/tenant/components/ExtendedLandlordInformationContent/constants';
import {
  Modal,
  ModalHeader,
  ModalBody,
  useModalHandle
} from '@spotahome/ui-library';

import { useLandlordInformation } from '../../context/LandlordInformationContext';
import { ListingSectionObservable } from '../ListingSection';

import './MeetYourLandlord.scss';

const MAIN_LANGUAGES = [
  'spanish',
  'english',
  'portuguese',
  'french',
  'german',
  'italian'
];

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const LANDLORD_ICONS = {
  [PRIVATE_LANDLORD]: () => <PrivateLandlord color="blue" />,
  [PROFESSIONAL_LANDLORD]: () => <ProfessionalLandlord color="blue" />
};

const RESPONSE_RATE_CONFIG = {
  4: {
    tagKey: 'homecard.label.response-rate.high',
    colorSchema: 'green',
    modalLevel: 'high',
    icon: 'quick_phrases'
  },
  3: {
    tagKey: 'homecard.label.response-rate.medium',
    colorSchema: 'blue',
    modalLevel: 'medium',
    icon: 'quick_phrases'
  },
  2: {
    tagKey: 'homecard.label.response-rate.low',
    colorSchema: 'amber',
    modalLevel: 'low',
    icon: 'quick_phrases'
  }
};

const MeetYourLandlord = ({ landlordScoring = EMPTY_OBJECT }) => {
  const { landlordInformation } = useLandlordInformation();
  const { isModalOpen, handleModalOpen, handleModalClose } = useModalHandle();
  const isBadgesResponseRateVariant =
    useSoyuzExperiment('BadgesResponseRateSearchAndListing').get('behaviour') ===
    'variant';

  if (!landlordInformation) {
    return null;
  }

  const {
    type,
    firstListingDate: joiningDate,
    listingsPublished,
    averageResponseTime,
    responseRateState,
    responseRatePercentage,
    languages = EMPTY_ARRAY
  } = landlordInformation;

  const timeWithSpotahome = joiningDate ? yearsSinceDate(joiningDate) : null;
  const hasScoring =
    landlordScoring?.score != null &&
    landlordScoring?.totalBookingsClosed != null;

  const filteredCommonLanguages = languages.filter(language =>
    MAIN_LANGUAGES.includes(language)
  );
  const spokenLanguagesTranslated = filteredCommonLanguages
    .map(language => trans(`landlord_language_${language}`))
    .join(', ');

  const rrConfig = RESPONSE_RATE_CONFIG[responseRateState];
  const responseRateTag =
    isBadgesResponseRateVariant && rrConfig != null
      ? {
          show: true,
          icon: 'quick_phrases',
          label: trans(rrConfig.tagKey),
          colorSchema: rrConfig.colorSchema,
          onClick: handleModalOpen
        }
      : {
          show: averageResponseTime != null,
          icon: 'quick_phrases',
          label: trans('landlord.info.responds.within', {
            time: averageResponseTime
          })
        };

  const tagItems = [
    responseRateTag,
    {
      show: timeWithSpotahome != null,
      icon: 'furnished',
      label: trans('landlord.info.years.with.spotahome', {
        years: timeWithSpotahome
      })
    },
    {
      show: listingsPublished != null,
      icon: 'add_ad',
      label: `${trans(
        'landlord.info.listings.on.spotahome'
      )} ${listingsPublished}`
    },
    {
      show: true,
      icon: 'emoji_language',
      label: trans('landlord.info.speaks', {
        languages:
          spokenLanguagesTranslated || trans('landlord_language_english')
      })
    }
  ];

  const tagsToShow = tagItems.filter(tag => tag.show);

  return (
    <ListingSectionObservable
      section="aboutLandlord"
      className="meet-your-landlord"
      bordered={false}
    >
      <div className="meet-your-landlord__content">
        <ListingTitle>{trans('listing.meet_your_landlord.title')}</ListingTitle>
        <div className="meet-your-landlord__header">
          <div className="meet-your-landlord__icon-wrapper">
            {LANDLORD_ICONS[type]?.()}
          </div>
          <div className="meet-your-landlord__title-block">
            <h3 className="meet-your-landlord__type">
              {trans(`listing.landlord_info.type.${type}`)}
            </h3>
            <div className="meet-your-landlord__meta">
              <span className="meet-your-landlord__verified">
                <DSIcon
                  className="meet-your-landlord__verified-icon"
                  name="verified_filled"
                  size="sm"
                />
                {trans('listing.landlord_info.verified')}
              </span>
              {hasScoring && (
                <Scoring
                  score={landlordScoring.score}
                  totalReviews={landlordScoring.totalBookingsClosed}
                  size="medium"
                  bold={false}
                  tooltipPosition="top"
                  iconClassName="meet-your-landlord__scoring-icon"
                  className="meet-your-landlord__scoring-text"
                />
              )}
            </div>
          </div>
        </div>

        <div className="meet-your-landlord__chips">
          {tagsToShow.map(tag => (
            <Tag
              key={tag.icon}
              label={tag.label}
              className="meet-your-landlord__chip"
              renderLeadingIcon={() => <DSIcon name={tag.icon} size="sm" />}
              onClick={tag.onClick || null}
              isSpaced
              size="big"
              colorSchema={tag.colorSchema || 'light'}
            />
          ))}
        </div>
      </div>

      {isBadgesResponseRateVariant && rrConfig != null && (
        <Modal
          contentLabel="response-rate-modal"
          isOpen={isModalOpen}
          size="medium"
          onRequestClose={handleModalClose}
        >
          <ModalHeader onRequestClose={handleModalClose} />
          <ModalBody>
            <div className={`response-rate-modal response-rate-modal--${rrConfig.colorSchema}`}>
              <span className="response-rate-modal__icon">
                <DSIcon name={rrConfig.icon} />
              </span>
              <h2 className="response-rate-modal__title">
                {trans(`property_show.book_now_card.response_rate.modal.title.${rrConfig.modalLevel}`)}
              </h2>
              <div className="response-rate-modal__percentage-box">
                <span className="response-rate-modal__percentage-number">
                  {responseRatePercentage != null ? `${responseRatePercentage}%` : '—'}
                </span>
                <span className="response-rate-modal__percentage-label">
                  {trans('property_show.book_now_card.response_rate.modal.percentage.label')}
                </span>
              </div>
              <p className="response-rate-modal__footer">
                {trans(`property_show.book_now_card.response_rate.modal.footer.${rrConfig.modalLevel}`)}
              </p>
            </div>
          </ModalBody>
        </Modal>
      )}
    </ListingSectionObservable>
  );
};

MeetYourLandlord.propTypes = {
  landlordScoring: PropTypes.shape(LandlordScoringShape)
};

export default MeetYourLandlord;
